import { useCallback } from 'react';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { WORKSPACE_WHITELABEL_THEME_NAME } from '@/hooks/useThemeConfig';
import {
    defaultDarkComponentTokens,
    defaultDarkTokens,
    defaultLightComponentTokens,
    defaultLightTokens,
    platformDarkAlgorithm,
    platformLightAlgorithm,
} from '@/theme';
import type { WhitelabelWorkspaceThemeSettings } from '@/types';

export const WORKSPACE_WHITELABEL_LIGHT_THEME_NAME = 'workspace-whitelabel-light';
export const WORKSPACE_WHITELABEL_DARK_THEME_NAME = 'workspace-whitelabel-dark';

export const useWorkspaceWhitelabelTheme = () => {
    const { registerTheme, setTheme, setWorkspaceThemeConfiguration } = useAntDesignTheme();

    const configureWorkspaceTheme = useCallback(
        (settings: WhitelabelWorkspaceThemeSettings) => {
            registerTheme({
                name: WORKSPACE_WHITELABEL_LIGHT_THEME_NAME,
                algorithm: platformLightAlgorithm,
                components: defaultLightComponentTokens,
                tokens: {
                    ...defaultLightTokens,
                    ...settings.light,
                },
            });

            registerTheme({
                name: WORKSPACE_WHITELABEL_DARK_THEME_NAME,
                algorithm: platformDarkAlgorithm,
                components: defaultDarkComponentTokens,
                tokens: {
                    ...defaultDarkTokens,
                    ...settings.dark,
                },
            });

            setWorkspaceThemeConfiguration({
                darkThemeName: WORKSPACE_WHITELABEL_DARK_THEME_NAME,
                lightThemeName: WORKSPACE_WHITELABEL_LIGHT_THEME_NAME,
                supportsColorSchemes: !settings.reuseLightThemeForDark,
            });
        },
        [registerTheme, setWorkspaceThemeConfiguration],
    );

    const activateWorkspaceTheme = useCallback(() => {
        setTheme(WORKSPACE_WHITELABEL_THEME_NAME);
    }, [setTheme]);

    const clearWorkspaceTheme = useCallback(() => {
        setWorkspaceThemeConfiguration(undefined);
    }, [setWorkspaceThemeConfiguration]);

    const deactivateWorkspaceTheme = useCallback(
        (fallbackTheme: 'dark' | 'light', activeThemeName: string) => {
            setWorkspaceThemeConfiguration(undefined);

            if (activeThemeName === WORKSPACE_WHITELABEL_THEME_NAME) {
                setTheme(fallbackTheme);
            }
        },
        [setTheme, setWorkspaceThemeConfiguration],
    );

    return {
        activateWorkspaceTheme,
        clearWorkspaceTheme,
        configureWorkspaceTheme,
        deactivateWorkspaceTheme,
    };
};
